























































import type { PropType } from '@nuxtjs/composition-api';
import { computed, defineComponent, ref } from '@nuxtjs/composition-api';

import { useProductGroup } from '~/diptyqueTheme/composable/useProductGroup';
import { useScreenSize } from '~/diptyqueTheme/composable/useScreenSize';
import { Logger } from '~/helpers/logger';
import type {
  ProductsGroupModel,
  ProductSliderModel,
  VisualModel
} from '~/integrations/contentful/src/types';

enum GroudDisplayEnum {
  SLIDER = 'slider',
  GRID = 'grid'
}

type GroupDisplay = `${GroudDisplayEnum}`;

interface TabData {
  title: string;
  id: string;
  data: {
    display: GroupDisplay;
    sku: string[];
    type: string;
    nostoId: string;
    altVisuals: VisualModel[];
  };
}

export default defineComponent({
  name: 'ProductSlider',
  components: {
    VaimoHeading: () => import('atoms/VaimoHeading.vue'),
    VaimoProducts: () => import('organisms/VaimoProducts.vue'),
    VaimoTabs: () => import('molecules/VaimoTabs.vue'),
    VaimoTruncatedText: () => import('atoms/VaimoTruncatedText.vue'),
    VaimoButtons: () => import('molecules/VaimoButtons.vue')
  },
  props: {
    uniqueKey: {
      required: false,
      type: String,
      default: ''
    },
    sectionData: {
      required: true,
      type: Object as PropType<ProductSliderModel>,
      default: () => ({
        etitle: '',
        heading: '',
        headingLink: '',
        descr: '',
        productGroupsCollection: {
          items: []
        },
        buttonCollection: {
          items: []
        }
      })
    },
    config: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  setup(props) {
    const wrapperProps = ref({
      slider: {
        mobileShift: 25,
        showDots: true,
        arrows: true,
        arrowsSize: 20,
        arrowsIconPrev: 'chevronleft',
        arrowsIconNext: 'chevronright',
        arrowsType: 'contextual'
      }
    });
    const { isDesktop } = useScreenSize();
    const {
      getTransformedData,
      isUpsellProductGroup,
      shouldHideForSingleNostoGroup
    } = useProductGroup(props.config);
    const productsGroups = computed<ProductsGroupModel[]>(() => {
      try {
        const items = props.sectionData?.productGroupsCollection?.items || [];
        return getTransformedData(items);
      } catch (e) {
        Logger.error('[ProductSlider] Failed to get transformed data:', e);
        return [];
      }
    });
    const heading = computed<string>(() => props.sectionData?.heading || '');
    const headingLink = computed<string>(
      () => props.sectionData?.headingLink || ''
    );
    const hasLoaded = ref<boolean>(false);
    const setLoading = (flag: boolean) => {
      hasLoaded.value = flag;
    };

    const shouldHideSlider = computed<boolean>(() => {
      // If no products groups or only one group with no SKUs, hide slider
      if (!productsGroups.value || productsGroups.value.length === 0) {
        return true;
      }

      // If only one group and it's a Nosto group with no SKUs, hide slider
      if (shouldHideForSingleNostoGroup(productsGroups.value)) {
        return true;
      }

      // Otherwise, show slider
      return false;
    });

    const getGroupDisplay = (group: ProductsGroupModel): GroupDisplay => {
      if (isUpsellProductGroup(group) && !isDesktop.value) {
        return GroudDisplayEnum.GRID;
      }

      return GroudDisplayEnum.SLIDER;
    };

    // Filter out tabs that are empty or have no SKUs
    const isTabEmpty = (tab: TabData): boolean => {
      return tab.data.sku?.length > 0 || tab.data.type !== 'list of sku';
    };

    const getTabSku = (group: ProductsGroupModel): string[] => {
      if (!group.sku) {
        return [];
      }

      return Array.isArray(group.sku) ? group.sku : [group.sku];
    };

    const tabs = computed<TabData[]>(() => {
      const tabsData = productsGroups.value.map((group, i) => ({
        title: group.groupTitle,
        id: 'tab' + i,
        data: {
          display: getGroupDisplay(group),
          sku: getTabSku(group),
          type: group.type,
          nostoId: group?.nostoSlot || '',
          altVisuals: group?.altvisCollection?.items || []
        }
      }));

      return tabsData.filter(isTabEmpty);
    });

    return {
      wrapperProps,
      heading,
      headingLink,
      tabs,
      hasTabs: computed(() => tabs.value.length > 0),
      setLoading,
      hasLoaded,
      shouldHideSlider
    };
  }
});
